import { useState, useEffect } from 'react';
import { Typography, Spin, Empty, Tag } from 'antd';
import { useEventSelection } from '../../../contexts/useEventSelection';
import sessionsService, { type Session } from '../../../api/sessionsService';
import type { WidgetProps } from './types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

export const ScheduleWidget = ({ sizeClass }: WidgetProps) => {
  const { selectedEvent } = useEventSelection();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);

  const tz = selectedEvent?.circuit?.time_zone || 'UTC';

  useEffect(() => {
    if (!selectedEvent) return;

    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await sessionsService.getSessionsByEvent(
          selectedEvent.season_year,
          selectedEvent.circuit_id,
          selectedEvent.start_date,
        );
        if (!cancelled) setSessions(data);
      } catch {
        if (!cancelled) setSessions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [selectedEvent]);

  if (loading) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 11 }}>No event selected</span>} />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 11 }}>No sessions scheduled</span>} />
      </div>
    );
  }

  const isCompact = sizeClass === 'compact';
  const now = dayjs().tz(tz);

  const grouped = sessions.reduce<Record<string, Session[]>>((acc, session) => {
    const dateKey = dayjs.utc(session.start_time).tz(tz).format('YYYY-MM-DD');
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(session);
    return acc;
  }, {});

  const sortedDays = Object.keys(grouped).sort();

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: isCompact ? 6 : 10 }}>
      {sortedDays.map((dateKey) => {
        const daySessions = grouped[dateKey];
        const dayLabel = dayjs(dateKey).format('ddd DD MMM');

        return (
          <div key={dateKey} style={{ marginBottom: isCompact ? 8 : 12 }}>
            <Text strong style={{ fontSize: isCompact ? 11 : 12, display: 'block', marginBottom: 4 }}>
              {dayLabel}
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: isCompact ? 2 : 4 }}>
              {daySessions.map((session, idx) => {
                const start = dayjs.utc(session.start_time).tz(tz);
                const end = dayjs.utc(session.end_time).tz(tz);
                const isActive = now.isAfter(start) && now.isBefore(end);
                const isPast = now.isAfter(end);

                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: isCompact ? '2px 4px' : '3px 6px',
                      borderRadius: 4,
                      background: isActive ? 'rgba(82, 196, 26, 0.1)' : 'transparent',
                      opacity: isPast ? 0.5 : 1,
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{
                        fontSize: isCompact ? 10 : 11,
                        minWidth: isCompact ? 65 : 80,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {start.format('HH:mm')} - {end.format('HH:mm')}
                    </Text>
                    <Text
                      style={{
                        fontSize: isCompact ? 11 : 12,
                        fontWeight: isActive ? 600 : 400,
                        flex: 1,
                      }}
                    >
                      {sessionsService.getSessionDisplayName(session.session_name)}
                    </Text>
                    {isActive && (
                      <Tag
                        color="success"
                        style={{
                          fontSize: 9,
                          lineHeight: '14px',
                          padding: '0 4px',
                          margin: 0,
                          borderRadius: 3,
                        }}
                      >
                        LIVE
                      </Tag>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
